import * as Sentry from '@sentry/react';

export const ASSET_RELOAD_KEY = 'asset-reload-attempted';
export const ASSET_RESET_KEY = 'asset-reset-attempted';
export const ASSET_RECOVERY_STARTED_KEY = 'asset-recovery-started-at';

type RecoveryAction = 'reset' | 'exhausted';

const RECOVERY_STATE_MAX_AGE_MS = 2 * 60 * 1000;
const RECOVERY_CLEAR_DELAY_MS = 30 * 1000;
const RECOVERABLE_ASSET_RETRY_DELAY_MS = 300;
const RECOVERY_URL_PARAM = 'gt-recovery';

const RECOVERABLE_ASSET_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  // WebKit (iOS/Safari) rejects a cross-origin chunk import with this message
  // when the CORS check fails (transient network/proxy/cache-variant issues on
  // the static CDN).
  /Cross-origin script load denied by Cross-Origin Resource Sharing policy/i,
  /error loading dynamically imported module/i,
  /Loading chunk [\w-]+ failed/i,
  /ChunkLoadError/i,
  /Failed to load module script/i,
  /Expected .*module script/i,
  /MIME type .*text\/html/i,
];

function safeGetSessionValue(key: string) {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetSessionValue(key: string, value: string) {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // If sessionStorage is unavailable, still attempt the recovery action.
  }
}

function safeRemoveSessionValue(key: string) {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // No-op: private browsing or storage restrictions should not break boot.
  }
}

function safeGetPersistentValue(key: string) {
  try {
    return localStorage.getItem(key);
  } catch {
    return safeGetSessionValue(key);
  }
}

function safeSetPersistentValue(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    safeSetSessionValue(key, value);
  }
}

function safeRemovePersistentValue(key: string) {
  try {
    localStorage.removeItem(key);
  } catch {
    // No-op: sessionStorage fallback is cleared below.
  }

  safeRemoveSessionValue(key);
}

function clearRecoveryUrlParam() {
  try {
    const url = new URL(window.location.href);

    if (!url.searchParams.has(RECOVERY_URL_PARAM)) {
      return;
    }

    url.searchParams.delete(RECOVERY_URL_PARAM);
    window.history.replaceState(window.history.state, '', url.toString());
  } catch {
    // No-op: URL cleanup is best-effort and should not block manual retries.
  }
}

function getErrorMessage(error: unknown): string {
  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String(error.message);
  }

  return '';
}

function isSameOriginUrl(url: string) {
  try {
    return new URL(url, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
}

// Origin of the static asset CDN (VITE_STATIC_URL), when build assets are served
// cross-origin. Null when unset (assets stay same-origin).
const STATIC_ASSET_ORIGIN = (() => {
  try {
    const value = import.meta.env.VITE_STATIC_URL;
    return value ? new URL(value).origin : null;
  } catch {
    return null;
  }
})();

/**
 * True for URLs we control and can recover by reloading: same-origin, or the
 * cross-origin static CDN. A failed CDN chunk would otherwise be ignored by the
 * same-origin element filter.
 */
function isAppManagedAssetUrl(url: string) {
  if (isSameOriginUrl(url)) {
    return true;
  }
  if (!STATIC_ASSET_ORIGIN) {
    return false;
  }
  try {
    return new URL(url, window.location.href).origin === STATIC_ASSET_ORIGIN;
  } catch {
    return false;
  }
}

export function markAssetReloadAttempted() {
  safeSetPersistentValue(ASSET_RELOAD_KEY, 'true');
}

export function clearAssetRecoveryAttempts() {
  safeRemovePersistentValue(ASSET_RELOAD_KEY);
  safeRemovePersistentValue(ASSET_RESET_KEY);
  safeRemovePersistentValue(ASSET_RECOVERY_STARTED_KEY);
  clearRecoveryUrlParam();
}

export function scheduleAssetRecoveryClear() {
  if (!hasFreshRecoveryAttempt()) {
    clearAssetRecoveryAttempts();
    return undefined;
  }

  const timeoutId = window.setTimeout(() => {
    clearAssetRecoveryAttempts();
  }, RECOVERY_CLEAR_DELAY_MS);

  return () => {
    window.clearTimeout(timeoutId);
  };
}

export function isRecoverableAssetLoadError(error: unknown) {
  const message = getErrorMessage(error);

  return RECOVERABLE_ASSET_ERROR_PATTERNS.some((pattern) =>
    pattern.test(message),
  );
}

/**
 * Runs `load`, and gives it a second chance when it fails with a recoverable
 * asset error. A chunk import fails for transient reasons far more often than
 * for permanent ones — a flaky connection, a deploy swapping the files
 * mid-session — and one retry clears most of them without paying for the full
 * service-worker/cache wipe and reload `requestAssetRecovery` performs.
 *
 * Anything that is not a recoverable asset error is rethrown untouched: only
 * the caller knows what to do with it.
 */
export async function retryRecoverableAssetLoad<T>(
  load: () => Promise<T>,
  onRetry?: (error: unknown) => void,
): Promise<T> {
  try {
    return await load();
  } catch (error) {
    if (!isRecoverableAssetLoadError(error)) {
      throw error;
    }

    onRetry?.(error);
    await new Promise((resolve) => {
      setTimeout(resolve, RECOVERABLE_ASSET_RETRY_DELAY_MS);
    });

    return load();
  }
}

export function isRecoverableAssetElement(target: EventTarget | null) {
  if (target instanceof HTMLScriptElement) {
    return Boolean(target.src) && isAppManagedAssetUrl(target.src);
  }

  if (target instanceof HTMLLinkElement) {
    const isCriticalLink = ['modulepreload', 'preload', 'stylesheet'].includes(
      target.rel,
    );

    return (
      isCriticalLink &&
      Boolean(target.href) &&
      isAppManagedAssetUrl(target.href)
    );
  }

  return false;
}

export function addAssetRecoveryBreadcrumb(
  message: string,
  data?: Record<string, unknown>,
  level: Sentry.SeverityLevel = 'info',
) {
  Sentry.addBreadcrumb({
    category: 'asset-recovery',
    message,
    level,
    data,
  });
}

async function unregisterAppServiceWorkers() {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  const registrations = await navigator.serviceWorker.getRegistrations();

  await Promise.all(
    registrations.map(async (registration) => {
      const scriptURL =
        registration.active?.scriptURL ||
        registration.installing?.scriptURL ||
        registration.waiting?.scriptURL ||
        '';

      if (!scriptURL || isSameOriginUrl(scriptURL)) {
        await registration.unregister();
      }
    }),
  );
}

async function deleteAppCaches() {
  if (!('caches' in window)) {
    return;
  }

  const cacheNames = await caches.keys();
  const appCacheNames = cacheNames.filter(
    (cacheName) =>
      cacheName.includes('workbox') ||
      cacheName.includes('precache') ||
      cacheName.includes('app-static-assets') ||
      cacheName.includes('app-images-cache') ||
      cacheName.includes('google-fonts-cache') ||
      cacheName.includes('gstatic-fonts-cache'),
  );

  await Promise.all(appCacheNames.map((cacheName) => caches.delete(cacheName)));
}

function reloadWithCacheBust() {
  const url = new URL(window.location.href);
  url.searchParams.set(RECOVERY_URL_PARAM, Date.now().toString());
  window.location.replace(url.toString());
}

function isFreshRecoveryTimestamp(timestamp: number) {
  const age = Date.now() - timestamp;

  return (
    Number.isFinite(timestamp) && age >= 0 && age < RECOVERY_STATE_MAX_AGE_MS
  );
}

function getRecoveryTimestampFromUrl() {
  try {
    return Number(
      new URL(window.location.href).searchParams.get(RECOVERY_URL_PARAM),
    );
  } catch {
    return Number.NaN;
  }
}

function hasFreshRecoveryAttempt() {
  const startedAt = Number(safeGetPersistentValue(ASSET_RECOVERY_STARTED_KEY));

  return (
    isFreshRecoveryTimestamp(startedAt) ||
    isFreshRecoveryTimestamp(getRecoveryTimestampFromUrl())
  );
}

function markRecoveryStarted() {
  safeSetPersistentValue(ASSET_RECOVERY_STARTED_KEY, Date.now().toString());
}

export async function resetServiceWorkerAndCaches(
  reason: string,
  error?: unknown,
) {
  addAssetRecoveryBreadcrumb(
    'resetting service worker and caches',
    { reason, errorMessage: getErrorMessage(error) },
    'warning',
  );

  await unregisterAppServiceWorkers();
  await deleteAppCaches();
}

export function requestAssetRecovery(
  reason: string,
  error?: unknown,
): RecoveryAction {
  addAssetRecoveryBreadcrumb(
    'recoverable asset load failure',
    { reason, errorMessage: getErrorMessage(error) },
    'warning',
  );

  if (hasFreshRecoveryAttempt()) {
    return 'exhausted';
  }

  markAssetReloadAttempted();
  safeSetPersistentValue(ASSET_RESET_KEY, 'true');
  markRecoveryStarted();
  void resetServiceWorkerAndCaches(reason, error)
    .catch((resetError) => {
      addAssetRecoveryBreadcrumb(
        'service worker and cache reset failed',
        { reason, errorMessage: getErrorMessage(resetError) },
        'error',
      );
      Sentry.captureException(resetError);
    })
    .finally(() => {
      reloadWithCacheBust();
    });

  return 'reset';
}
